// src/pages/ResultDetailView.tsx
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button } from '../components/common/Button';
import { useLang } from '../context/LangContext';
import type { ResultDetail, QuestionAnswer } from '../api/types';

// Палитра цветомера: 0 КрО, 1 ЛиЖ, 2 ТрЗ, 3 МаЛ, 4 ТёС, 5 ТёК, 6 АсС, 7 ЧёС
const DETAIL_COLORS = ['#FA6400', '#FFFF00', '#00FF00', '#FF007C', '#0000CD', '#A0522D', '#808080', '#000000'];

interface Props {
  data: ResultDetail[];
  onClose: () => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const ViewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ViewTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  flex: 1;
`;

const TestSection = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

const TestSectionHeader = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const TestSectionTitle = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const ParticipantList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const ParticipantChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.primary}18;
  border: 1px solid ${({ theme }) => theme.colors.primary}44;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.text};
`;

const ChipDate = styled.span`
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 0.78rem;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  background: ${({ theme }) => theme.colors.background};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  flex-wrap: wrap;
`;

const FilterInput = styled.input`
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.cardBackground};
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.88rem;
  width: 240px;
`;

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.88rem;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  user-select: none;
`;

const CountText = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textLight};
  margin-left: auto;
`;

const TableWrapper = styled.div`
  overflow: auto;
  max-height: 60vh;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
`;

const Th = styled.th`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.cardBackground};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  text-align: left;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textLight};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
  z-index: 1;
  min-width: 80px;
`;

const QuestionTh = styled(Th)`
  min-width: 240px;
  max-width: 360px;
`;

const Td = styled.td`
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.md}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text};
  vertical-align: top;
`;

const QuestionTd = styled(Td)`
  max-width: 360px;
`;

const QuestionText = styled.div`
  max-width: 340px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.85rem;
`;

const AnswerCell = styled.div<{ $hasAnswer: boolean }>`
  color: ${({ $hasAnswer, theme }) =>
    $hasAnswer ? theme.colors.text : theme.colors.textLight};
  font-size: 0.85rem;
`;

const Coeff = styled.span`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.textLight};
  margin-left: 4px;
`;

const TimeSpan = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: 1px;
`;

const CustomAnswerRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-top: 4px;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.text};
  font-style: italic;
`;

const CustomAnswerBadge = styled.span`
  font-style: normal;
  font-size: 0.72rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary}18;
  border: 1px solid ${({ theme }) => theme.colors.primary}44;
  border-radius: 4px;
  padding: 0 5px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ColorAnswerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const ColorSwatch = styled.span<{ $color: string }>`
  display: inline-block;
  width: 18px;
  height: 18px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
  border: 1px solid rgba(0,0,0,0.18);
  flex-shrink: 0;
  vertical-align: middle;
`;

const IcBadge = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary}18;
  border: 1px solid ${({ theme }) => theme.colors.primary}44;
  border-radius: 4px;
  padding: 1px 5px;
  white-space: nowrap;
`;

const ColParticipant = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;
const ColDate = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textLight};
`;

// Per-test section component
const TestSectionView: React.FC<{ results: ResultDetail[] }> = ({ results }) => {
  const { t, lang } = useLang();
  const dateLocale = lang === 'ru' ? 'ru' : 'en';
  const [questionSearch, setQuestionSearch] = useState('');
  const [onlyAnswered, setOnlyAnswered] = useState(false);

  const allQuestions = useMemo(() => {
    const map = new Map<number, QuestionAnswer>();
    results.forEach(r =>
      r.answers.forEach(a => {
        if (!map.has(a.question_id)) map.set(a.question_id, a);
      }),
    );
    return Array.from(map.values()).sort(
      (a, b) => a.question_order - b.question_order,
    );
  }, [results]);

  const filteredQuestions = useMemo(() => {
    let qs = allQuestions;
    if (questionSearch.trim()) {
      const q = questionSearch.trim().toLowerCase();
      qs = qs.filter(qq => qq.question_content.toLowerCase().includes(q));
    }
    if (onlyAnswered) {
      qs = qs.filter(qq =>
        results.some(r => {
          const ans = r.answers.find(a => a.question_id === qq.question_id);
          return ans?.option_id != null || ans?.color_answer != null;
        }),
      );
    }
    return qs;
  }, [allQuestions, questionSearch, onlyAnswered, results]);

  const testTitle = results[0]?.test_title || `${t('detailColStimulus')} #${results[0]?.test_id}`;

  return (
    <TestSection>
      <TestSectionHeader>
        <TestSectionTitle>{testTitle}</TestSectionTitle>
        <ParticipantList>
          {results.map(r => (
            <ParticipantChip key={r.result_id}>
              {r.participant_name || r.username || `#${r.result_id}`}
              {r.completed_at && (
                <ChipDate>
                  · {new Date(r.completed_at).toLocaleDateString(dateLocale)}
                </ChipDate>
              )}
            </ParticipantChip>
          ))}
        </ParticipantList>
      </TestSectionHeader>

      <FilterBar>
        <FilterInput
          placeholder={t('detailSearch')}
          value={questionSearch}
          onChange={e => setQuestionSearch(e.target.value)}
        />
        <CheckLabel>
          <input
            type="checkbox"
            checked={onlyAnswered}
            onChange={e => setOnlyAnswered(e.target.checked)}
          />
          {t('detailOnlyAnswered')}
        </CheckLabel>
        <CountText>
          {filteredQuestions.length} / {allQuestions.length} {t('detailStimuliLabel')}
        </CountText>
      </FilterBar>

      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <Th style={{ width: 40 }}>{t('detailColN')}</Th>
              <QuestionTh>{t('detailColStimulus')}</QuestionTh>
              {results.map(r => (
                <Th key={r.result_id}>
                  <ColParticipant>
                    {r.participant_name || r.username || `#${r.result_id}`}
                  </ColParticipant>
                  {r.completed_at && (
                    <ColDate>
                      {new Date(r.completed_at).toLocaleDateString(dateLocale)}
                    </ColDate>
                  )}
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredQuestions.length === 0 ? (
              <tr>
                <Td
                  colSpan={results.length + 2}
                  style={{ textAlign: 'center', padding: '1.5rem' }}
                >
                  {t('detailNoStimuli')}
                </Td>
              </tr>
            ) : (
              filteredQuestions.map(q => (
                <tr key={q.question_id}>
                  <Td>{q.question_order}</Td>
                  <QuestionTd>
                    <QuestionText title={q.question_content}>
                      {q.question_content}
                    </QuestionText>
                  </QuestionTd>
                  {results.map(r => {
                    const ans = r.answers.find(
                      a => a.question_id === q.question_id,
                    );
                    const ca = ans?.color_answer;
                    const hasAnswer =
                      ans?.option_content != null ||
                      ans?.coefficient != null ||
                      ca != null;
                    return (
                      <Td key={r.result_id}>
                        {ca ? (
                          <>
                            <ColorAnswerRow>
                              <ColorSwatch $color={DETAIL_COLORS[ca.first]} title={`#${ca.first + 1}: ${DETAIL_COLORS[ca.first]}`} />
                              <ColorSwatch $color={DETAIL_COLORS[ca.second]} title={`#${ca.second + 1}: ${DETAIL_COLORS[ca.second]}`} />
                              <IcBadge>ИЦ {ca.ic}</IcBadge>
                            </ColorAnswerRow>
                            <TimeSpan>{ca.time_first} + {ca.time_second} = {ca.time_total} {t('detailMs')}</TimeSpan>
                          </>
                        ) : (
                          <AnswerCell $hasAnswer={hasAnswer}>
                            {ans && ans.option_content != null ? (
                              <>
                                {(ans.click_options && ans.click_options.length > 1)
                                  ? ans.click_options.map((co, ci) => (
                                      <div key={`${co.option_id}-${ci}`}>
                                        {co.option_content}
                                        {co.coefficient != null && <Coeff>({co.coefficient})</Coeff>}
                                      </div>
                                    ))
                                  : (
                                    <>
                                      {ans.option_content}
                                      {ans.coefficient != null && (
                                        <Coeff>({ans.coefficient})</Coeff>
                                      )}
                                    </>
                                  )}
                              </>
                            ) : ans?.coefficient != null ? (
                              <span>{ans.coefficient}</span>
                            ) : (
                              ans?.skip_reason ?? '—'
                            )}
                          </AnswerCell>
                        )}
                        {ans?.custom_answer && (
                          <CustomAnswerRow>
                            <CustomAnswerBadge>{t('detailCustomAnswerLabel')}</CustomAnswerBadge>
                            {ans.custom_answer}
                          </CustomAnswerRow>
                        )}
                        {!ca && ans?.question_time != null && (
                          <TimeSpan>{ans.question_time} {t('detailMs')}</TimeSpan>
                        )}
                      </Td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrapper>
    </TestSection>
  );
};

const ResultDetailView: React.FC<Props> = ({ data, onClose }) => {
  const { t } = useLang();
  const groupedByTest = useMemo(() => {
    const map = new Map<number, ResultDetail[]>();
    data.forEach(r => {
      const list = map.get(r.test_id);
      if (list) {
        list.push(r);
      } else {
        map.set(r.test_id, [r]);
      }
    });
    return map;
  }, [data]);

  return (
    <Container>
      <ViewHeader>
        <Button variant="outline" size="sm" onClick={onClose}>
          {t('detailBack')}
        </Button>
        <ViewTitle>
          {t('detailTitle')} ({data.length}{' '}
          {data.length === 1 ? t('detailParticipant1') : t('detailParticipantsN')})
        </ViewTitle>
      </ViewHeader>

      {Array.from(groupedByTest.entries()).map(([testId, results]) => (
        <TestSectionView key={testId} results={results} />
      ))}
    </Container>
  );
};

export default ResultDetailView;
